// Room/game-state bookkeeping, ported from the old Node lobby server
// (server/src/rooms.ts) to run inside the host player's own tab. The host's
// browser is now the authority for a room instead of a Node process; see
// hostRoom.ts for the message-handling switch that drives this.

import { DAY_LEN } from '@world/weather';
import type { GameSettings, PlayerInfo, RoomState, ServerMessage, Transform } from './protocol';

const ROOM_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I

const DEFAULT_SETTINGS: GameSettings = {
  seed: 2136,
  worldLabel: 'Heron Lake',
  rivalCount: 0,
  useFixedMap: false,
};

// Mirrors world/weather.ts's initial `gameTime` (DAY_LEN * 0.4) — the value
// every client's clock starts at before a game begins, so the host's
// real-time floor lines up with it at start_game.
const BASE_GAME_TIME = DAY_LEN * 0.4;

export function generateRoomCode(): string {
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += ROOM_CODE_CHARS[Math.floor(Math.random() * ROOM_CODE_CHARS.length)];
  }
  return code;
}

// Mirrored (as plain ids only — no names/visuals/stats) from
// client/src/entities/avatars.ts so the room can enforce one-per-player
// without knowing what an avatar actually is.
export const AVATAR_IDS = ['old-genji', 'ume', 'taro', 'kenji', 'hana', 'sachi'];

export interface Player {
  id: string;
  name: string;
  ready: boolean;
  avatarId: string;
  sendReliable: (msg: ServerMessage) => void;
  sendUnreliable: (msg: ServerMessage) => void;
}

export interface PlayerGameState {
  transform: Transform;
  boat: Transform | null;
  cart: Transform | null;
  score: number;
}

export interface JettyPlacement {
  cells: Array<{ q: number; r: number }>;
  angle: number;
}

export interface ShelterPlacement {
  id: string;
  x: number;
  z: number;
  heading: number;
}

export class Room {
  readonly code: string;
  hostId: string;
  settings: GameSettings = { ...DEFAULT_SETTINGS };
  started = false;

  private gameStartRealMs: number | null = null;
  private readonly players = new Map<string, Player>();
  private readonly gameStates = new Map<string, PlayerGameState>();
  private readonly jettyPlacements: JettyPlacement[] = [];
  private readonly shelterPlacements: ShelterPlacement[] = [];

  constructor(code: string, host: Player) {
    this.code = code;
    this.hostId = host.id;
    this.players.set(host.id, host);
  }

  get size(): number {
    return this.players.size;
  }

  startClock(): void {
    this.gameStartRealMs = Date.now();
  }

  currentGameTime(): number {
    if (this.gameStartRealMs === null) return BASE_GAME_TIME;
    return BASE_GAME_TIME + (Date.now() - this.gameStartRealMs) / 1000;
  }

  isEmpty(): boolean {
    return this.players.size === 0;
  }

  getPlayer(id: string): Player | undefined {
    return this.players.get(id);
  }

  addPlayer(player: Player): void {
    this.players.set(player.id, player);
  }

  removePlayer(playerId: string): void {
    this.players.delete(playerId);
    this.gameStates.delete(playerId);
    if (this.hostId === playerId) {
      const next = this.players.keys().next();
      if (!next.done) this.hostId = next.value;
    }
  }

  updateTransform(id: string, transform: Transform, boat: Transform | null, cart: Transform | null): void {
    const existing = this.gameStates.get(id);
    this.gameStates.set(id, { transform, boat, cart, score: existing?.score ?? 0 });
  }

  addJettyPlacement(placement: JettyPlacement): void {
    this.jettyPlacements.push(placement);
  }

  addShelterPlacement(placement: ShelterPlacement): void {
    this.shelterPlacements.push(placement);
  }

  updateScore(id: string, score: number): void {
    const existing = this.gameStates.get(id);
    if (existing) existing.score = score;
    else this.gameStates.set(id, { transform: { x: 0, z: 0, heading: 0 }, boat: null, cart: null, score });
  }

  setReady(playerId: string, ready: boolean): void {
    const player = this.players.get(playerId);
    if (player) player.ready = ready;
  }

  takenAvatarIds(excludingPlayerId?: string): Set<string> {
    const taken = new Set<string>();
    this.players.forEach((p) => { if (p.id !== excludingPlayerId) taken.add(p.avatarId); });
    return taken;
  }

  assignNextAvatar(): string {
    const taken = this.takenAvatarIds();
    return AVATAR_IDS.find((id) => !taken.has(id)) ?? AVATAR_IDS[0];
  }

  setAvatar(playerId: string, avatarId: string): { success: boolean } {
    const player = this.players.get(playerId);
    if (!player) return { success: false };
    if (this.takenAvatarIds(playerId).has(avatarId)) return { success: false };
    player.avatarId = avatarId;
    return { success: true };
  }

  updateSettings(partial: Partial<GameSettings>): void {
    this.settings = { ...this.settings, ...partial };
  }

  allReady(): boolean {
    return this.players.size > 0 && [...this.players.values()].every((p) => p.ready);
  }

  toState(): RoomState {
    return {
      code: this.code,
      hostId: this.hostId,
      settings: this.settings,
      started: this.started,
      players: [...this.players.values()].map(
        (p): PlayerInfo => ({ id: p.id, name: p.name, ready: p.ready, isHost: p.id === this.hostId, avatarId: p.avatarId }),
      ),
    };
  }

  broadcast(message: ServerMessage): void {
    for (const player of this.players.values()) player.sendReliable(message);
  }

  broadcastPath(message: Extract<ServerMessage, { type: 'player_path_update' }>): void {
    for (const player of this.players.values()) player.sendUnreliable(message);
  }
}
